import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { useShare } from 'core';

/**
 * «Поделиться результатом» (ТЗ 0039, шаг 7).
 *
 * 🔴 ПОРЯДОК ДЕЙСТВИЙ НА КНОПКЕ НЕЛЬЗЯ МЕНЯТЬ:
 *
 * 1. синхронно копируем текст в буфер — `navigator.clipboard` вне
 *    пользовательского жеста молча отклоняется, и студент вставил бы в пост
 *    чужой текст из буфера;
 * 2. синхронно открываем окно шеринга — `window.open` ПОСЛЕ `await` режет
 *    попап-блокер Safari, и кнопка выглядит мёртвой;
 * 3. и только потом, уже асинхронно, регистрируем нажатие.
 *
 * Оба шрама задокументированы в `pages/Diplomas`.
 *
 * ⚠️ Текст обязательно копируется, потому что передать его в окно шеринга
 * нельзя ни в одном канале: LinkedIn принимает только `url`, Facebook —
 * только `u`, у Instagram веб-эндпоинта шеринга нет вовсе.
 */
interface IGrowthShare {
  /** Предпросмотр: текст показываем, нажатие не засчитываем — бонус живёт
   *  на студенте, которого у смотрящего нет. */
  readOnly?: boolean;
}

export const GrowthShare = memo(({ readOnly = false }: IGrowthShare) => {
  const { t, i18n } = useTranslation();
  const { options, registerShare } = useShare(i18n.language === 'ru' ? 'RUS' : 'POL');
  const [reward, setReward] = useState<string[] | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // 🔴 Правки студента живут ЗДЕСЬ и копируется именно то, что на экране.
  // Пост уходит от его имени: диктовать формулировку и не дать поправить —
  // значит получить репост, под которым человеку неловко подписаться.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const onShare = useCallback(
    (channel: string, text: string, shareUrl: string | null) => {
      // 1 и 2 — синхронно, внутри жеста.
      navigator.clipboard?.writeText(text);
      setCopied(channel);
      if (shareUrl) {
        window.open(shareUrl, '_blank', 'noopener,noreferrer,width=680,height=640');
      }
      // 3 — асинхронно, уже после открытия окна.
      registerShare(channel).then((result) => {
        if (!result.counted) return;
        setReward([...result.grantedTitles, ...result.promoCodes]);
      });
    },
    [registerShare],
  );

  if (!options.length) return null;

  return (
    <section>
      <SectionTitle>{t('growth.share.title')}</SectionTitle>
      <Hint>{t('growth.share.hint')}</Hint>

      {reward && reward.length ? (
        <Reward>{t('growth.share.thanks', { list: reward.join(', ') })}</Reward>
      ) : null}

      <Grid>
        {options.map((row) => (
          <Card key={row.channel}>
            <ChannelName>
              {t(`growth.share.channel.${row.channel}`, {
                defaultValue: row.channel,
              })}
            </ChannelName>
            <Draft
              value={drafts[row.channel] ?? row.text}
              rows={4}
              onChange={(event) =>
                setDrafts((prev) => ({
                  ...prev,
                  [row.channel]: event.target.value,
                }))
              }
            />
            <Actions>
              {/* В предпросмотре бонус засчитывать некому: он живёт на
                  студенте. Текст поста при этом виден целиком — ради него
                  экран и открывают. */}
              {readOnly ? null : (
                <>
                  <ShareButton
                    type="button"
                    onClick={() =>
                      onShare(row.channel, drafts[row.channel] ?? row.text, row.shareUrl)
                    }>
                    {row.shareUrl ? t('growth.share.open') : t('growth.share.copyOnly')}
                  </ShareButton>
                  {/* 🔴 Называем награду, а не пишем «бонус доступен».
                      Медаль за репост требует ОДНОГО канала, поэтому за
                      второй и третий не полагается ничего — а экран обещал
                      бонус на всех трёх одинаково. Владелец: «если бонус
                      доступный, пусть указано, какой именно». */}
                  {row.bonusAvailable ? (
                    <BonusTag>
                      {row.bonusPromo
                        ? t('growth.share.bonusWithPromo', {
                            n: row.bonusPoints,
                          })
                        : t('growth.share.bonusPoints', { n: row.bonusPoints })}
                    </BonusTag>
                  ) : (
                    <Muted>
                      {row.bonusPoints || row.bonusPromo
                        ? t('growth.share.bonusUsed')
                        : t('growth.share.bonusNone')}
                    </Muted>
                  )}
                </>
              )}
            </Actions>
            {copied === row.channel ? <Muted>{t('growth.share.copied')}</Muted> : null}
          </Card>
        ))}
      </Grid>
    </section>
  );
});

const SectionTitle = styled.h2`
  margin: 0 0 6px;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Hint = styled.p`
  margin: 0 0 12px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Reward = styled.p`
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${Colors.bg_gray};
  /* signal_green (#37AB34) — цвет РАМКИ и значка успеха: как текст на белом
     это 2.98:1 при норме 4.5. Тёмно-зелёный marker_green даёт 7.36 в
     светлой теме и 6.93 в тёмной (правило в context/constants/colors.ts). */
  color: ${Colors.marker_green};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${Colors.gray};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChannelName = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const Draft = styled.textarea`
  /* 🔴 Глобального правила box-sizing в проекте нет: при width 100% отступы и
     рамка ПРИБАВЛЯЮТСЯ к ширине, и поле вылезало за карточку — ровно то, что
     владелец описал как «не влазит текст в input». */
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${Colors.gray};
  border-radius: 8px;
  padding: 8px 10px;
  resize: vertical;
  background: transparent;
  color: inherit;
  font: inherit;
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: auto;
`;

const ShareButton = styled.button`
  padding: 7px 16px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${Colors.main};
  background: transparent;
  /* Жёлтая рамка держит акцент, текст остаётся читаемым: жёлтым по белому
     это 1.33:1 (см. правило в context/constants/colors.ts). */
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const BonusTag = styled.span`
  /* signal_green (#37AB34) — цвет РАМКИ и значка успеха: как текст на белом
     это 2.98:1 при норме 4.5. Тёмно-зелёный marker_green даёт 7.36 в
     светлой теме и 6.93 в тёмной (правило в context/constants/colors.ts). */
  color: ${Colors.marker_green};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Muted = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;
